import * as _React from 'react'
import {useCallback, useEffect} from 'react'
import {StyleSheet, Text, View} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation, useRoute} from '@react-navigation/native'

import {usePalette} from '#/lib/hooks/usePalette'
import {useSession} from '#/state/session'
import {useSetMinimalShellMode} from '#/state/shell/minimal-mode'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {JitsiMeeting} from './components/JitsiMeeting'
import {useRooms} from './hooks/useRooms'

export function RoomCallScreen() {
  const pal = usePalette('default')
  const safeAreaInsets = useSafeAreaInsets()
  const route = useRoute<any>()
  const navigation = useNavigation()
  const {hasSession} = useSession()
  const setMinimalShell = useSetMinimalShellMode()
  const {_} = useLingui()
  const {rooms} = useRooms()

  // Support both RoomCall (roomId param) and P2pCall (callId param) routes
  const callId = route.params?.callId
  const roomId = route.params?.roomId || (callId ? `p2p-${callId}` : undefined)
  const routeRoomName = route.params?.roomName
  const isPrivate = route.params?.isPrivate || !!callId || roomId?.startsWith('p2p-')
  const warningDialogControl = Dialog.useDialogControl()

  // Resolve room name and tags — use route params immediately, fall back to rooms list
  const roomFromList = rooms.find(room => room.id === roomId)
  const roomName = isPrivate
    ? routeRoomName || 'Private Room'
    : roomFromList?.name || routeRoomName || ''
  const roomTags = isPrivate ? [] : roomFromList?.tags || []

  // Hide navigation when in a call
  useEffect(() => {
    setMinimalShell(true)

    const style = document.createElement('style')
    style.id = 'room-call-styles'
    style.innerHTML = `
      [role="navigation"],
      [style*="position: fixed"][style*="left: 50%"],
      [style*="position: fixed"][style*="bottom: 0"] {
        display: none !important;
        opacity: 0 !important;
        pointer-events: none !important;
        visibility: hidden !important;
      }
    `
    document.head.appendChild(style)

    return () => {
      setMinimalShell(false)
      document.getElementById('room-call-styles')?.remove()
    }
  }, [setMinimalShell])

  const handleRecordingDetected = useCallback(() => {
    console.log('⚠️ Screen recording detected - kicking user')
    warningDialogControl.open()
    setTimeout(() => {
      navigation.goBack()
    }, 3000)
  }, [navigation, warningDialogControl])

  const handleClose = () => {
    navigation.goBack()
  }

  if (!hasSession) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, pal.text]}>
            <Trans>You need to be logged in to join a video call</Trans>
          </Text>
        </View>
      </View>
    )
  }

  if (!roomId) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, pal.text]}>
            <Trans>Invalid room ID</Trans>
          </Text>
        </View>
      </View>
    )
  }

  return (
    <View style={[styles.container, {paddingTop: safeAreaInsets.top}]}>
      <JitsiMeeting
        roomId={roomId}
        roomName={roomName}
        roomTags={roomTags}
        onClose={handleClose}
        onRecordingDetected={handleRecordingDetected}
      />

      {/* Recording Warning Dialog */}
      <Dialog.Outer control={warningDialogControl}>
        <Dialog.Handle />
        <Dialog.ScrollableInner
          label={_(msg`Screen Recording Not Allowed`)}
          style={{backgroundColor: pal.view.backgroundColor}}>
          <View style={styles.warningContent}>
            <Text style={[styles.warningTitle, pal.text]}>
              <Trans>Screen Recording Detected</Trans>
            </Text>
            <Text style={[styles.warningText, pal.text]}>
              <Trans>
                Screen recording is only allowed in rooms with the 'Recording'
                tag.
              </Trans>
            </Text>
            <Text style={[styles.warningText, pal.textLight, {marginTop: 12}]}>
              <Trans>You have been disconnected from the call.</Trans>
            </Text>

            <View style={styles.warningButtons}>
              <Button
                label={_(msg`Close`)}
                variant="solid"
                color="primary"
                size="medium"
                onPress={() => {
                  warningDialogControl.close()
                  navigation.goBack()
                }}>
                <ButtonText>
                  <Trans>Close</Trans>
                </ButtonText>
              </Button>
            </View>
          </View>
          <Dialog.Close />
        </Dialog.ScrollableInner>
      </Dialog.Outer>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: '#000',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    color: 'white',
  },
  warningContent: {
    padding: 20,
  },
  warningTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  warningText: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
  },
  warningButtons: {
    marginTop: 24,
    alignItems: 'center',
  },
})
